import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Skeleton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { 
  Visibility, 
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Search,
  Delete as DeleteIcon,
  Person as PersonIcon,
  //People as PeopleIcon,
  DeleteSweep as DeleteSweepIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import EnhancedResultsViewer from '../components/analysis/EnhancedResultsViewer';
import { AnalysisJob } from '../types/index';
import { formatDateRange } from '../utils/dateUtils';
import { formatUserLabel } from '../utils/userDisplay';

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500); // 500ms debounce
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<AnalysisJob | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [userFilter, setUserFilter] = useState<'mine' | 'shared' | 'all'>('mine');
  
  // Multi-select state
  const [selectedJobIds, setSelectedJobIds] = useState<Set<number>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  const { data: jobsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['analysisJobs', page, rowsPerPage, debouncedSearch, userFilter],
    queryFn: async () => {
      if (userFilter === 'all') {
        // Fetch all users' jobs
        const response = await apiService.getAllAnalysisJobs();
        // Filter and paginate on frontend
        const filtered = debouncedSearch 
          ? response.filter((j: AnalysisJob) => 
              j.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
              j.id.toString().includes(debouncedSearch)
            )
          : response;
        const start = page * rowsPerPage;
        const jobs = filtered.slice(start, start + rowsPerPage);
        return { jobs, total: filtered.length, page, limit: rowsPerPage, pages: Math.ceil(filtered.length / rowsPerPage) };
      } else {
        // Fetch my own jobs, or those shared with me
        return apiService.getAnalysisJobs(
          page,
          rowsPerPage,
          debouncedSearch || undefined,
          userFilter === 'shared' ? 'shared' : 'own'
        );
      }
    },
    staleTime: 3000, // Consider data fresh for 3 seconds
    refetchInterval: 10000, // Auto-refresh every 10 seconds (reduced frequency)
  });

  // Extract jobs and pagination data
  const jobs = jobsResponse?.jobs || [];
  const totalJobs = jobsResponse?.total || 0;
  
  // Use jobs directly - server-side pagination
  const paginatedJobs = jobs;
  const filteredJobs = { length: totalJobs }; // For pagination display

  // Handle page change
  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle delete job
  const handleDeleteJob = async () => {
    if (!jobToDelete) return;
    
    try {
      setDeleting(true);
      await apiService.deleteAnalysisJob(jobToDelete.id);
      
      // Refresh the jobs list
      await refetch();
      
      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    } catch (err: any) {
      // Could add error toast here
    } finally {
      setDeleting(false);
    }
  };

  // Open delete confirmation
  const openDeleteDialog = (job: AnalysisJob) => {
    setJobToDelete(job);
    setDeleteDialogOpen(true);
  };

  // Toggle job selection
  const toggleJobSelection = (jobId: number) => {
    setSelectedJobIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  // Select all jobs on current page
  const selectAllOnPage = () => {
    const allSelected = paginatedJobs.every((job: AnalysisJob) => selectedJobIds.has(job.id));
    if (allSelected) {
      // Deselect all on current page
      setSelectedJobIds(prev => {
        const newSet = new Set(prev);
        paginatedJobs.forEach((job: AnalysisJob) => newSet.delete(job.id));
        return newSet;
      });
    } else {
      // Select all on current page
      setSelectedJobIds(prev => {
        const newSet = new Set(prev);
        paginatedJobs.forEach((job: AnalysisJob) => newSet.add(job.id));
        return newSet;
      });
    }
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedJobIds(new Set());
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      setDeleting(true);
      
      // Delete all selected jobs
      const deletePromises = Array.from(selectedJobIds).map(id => 
        apiService.deleteAnalysisJob(id)
      );
      
      await Promise.all(deletePromises);
      
      // Refresh the jobs list
      await refetch();
      
      // Close dialog and reset state
      setBulkDeleteDialogOpen(false);
      setSelectedJobIds(new Set());
    } catch (err: any) {
      console.error('Error deleting jobs:', err);
    } finally {
      setDeleting(false);
    }
  };

  // Check if all jobs on current page are selected
  const allOnPageSelected = paginatedJobs.length > 0 && paginatedJobs.every((job: AnalysisJob) => selectedJobIds.has(job.id));
  const someOnPageSelected = paginatedJobs.some((job: AnalysisJob) => selectedJobIds.has(job.id));

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'running':
        return <HourglassEmpty color="warning" />;
      default:
        return <></>;
    }
  };

  const getStatusColor = (status: string): "success" | "error" | "warning" | "default" => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (selectedJob) {
    return (
      <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, sm: 3, md: 4 }, py: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => setSelectedJob(null)}
            sx={{ mb: 2 }}
          >
            ← Back to Results List
          </Button>
        </Box>
        <EnhancedResultsViewer jobId={selectedJob} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 2 }}>
      <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Analysis Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all analysis jobs and completed reports
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button 
            variant="contained" 
            onClick={() => navigate('/analysis')}
            sx={{ ml: 2 }}
          >
            New Analysis
          </Button>
        </Box>
      </Box>

      {/* Filter and Search Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <ToggleButtonGroup
              value={userFilter}
              exclusive
              onChange={(_, newValue) => {
                if (newValue !== null) {
                  setUserFilter(newValue);
                  setPage(0);
                  // Selections refer to jobs on the previous tab; carrying them over
                  // would target jobs that are no longer listed
                  setSelectedJobIds(new Set());
                }
              }}
              size="small"
            >
              <ToggleButton value="mine">
                <PersonIcon sx={{ mr: 1 }} />
                My Jobs
              </ToggleButton>
              <ToggleButton value="shared">
                <ShareIcon sx={{ mr: 1 }} />
                Shared With Me
              </ToggleButton>
              {/*<ToggleButton value="all">
                <PeopleIcon sx={{ mr: 1 }} />
                All Jobs
              </ToggleButton>*/}
            </ToggleButtonGroup>

            <TextField
              variant="outlined"
              placeholder="Search by Device ID, Analysis Number, Creator…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              size="small"
              sx={{ flex: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm !== debouncedSearch && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          {debouncedSearch && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing {totalJobs} result{totalJobs !== 1 ? 's' : ''}
            </Typography>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load analysis jobs. Please try again.
        </Alert>
      )}

      {!isLoading && jobs && jobs.length === 0 && !debouncedSearch && (
        <Alert severity="info">
          {userFilter === 'shared'
            ? 'No analyses have been shared with you yet. When someone shares one, it will appear here.'
            : 'No analysis results yet. Start a new analysis to see results here.'}
        </Alert>
      )}

      {!isLoading && debouncedSearch && jobs.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No results found for "{debouncedSearch}". Try a different search term or clear the search to see all results.
        </Alert>
      )}

      {/* Selection Controls */}
      {/* Bulk selection drives bulk delete, which is owner-only - hidden on the shared tab */}
      {!isLoading && paginatedJobs.length > 0 && userFilter !== 'shared' && (
        <Box sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={allOnPageSelected}
                  indeterminate={someOnPageSelected && !allOnPageSelected}
                  onChange={selectAllOnPage}
                />
              }
              label={
                <Typography variant="body2">
                  Select All ({paginatedJobs.length})
                </Typography>
              }
            />
            {selectedJobIds.size > 0 && (
              <Chip 
                label={`${selectedJobIds.size} selected`}
                size="small"
                onDelete={clearSelection}
                color="primary"
              />
            )}
          </Box>
          
          {selectedJobIds.size > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setBulkDeleteDialogOpen(true)}
              size="small"
            >
              Delete Selected ({selectedJobIds.size})
            </Button>
          )}
        </Box>
      )}

      <Grid container spacing={3}>
        {isLoading ? (
          // Show skeleton loaders while loading
          Array.from(new Array(rowsPerPage)).map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={`skeleton-${index}`}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Skeleton variant="text" width="40%" height={32} />
                    <Skeleton variant="rectangular" width={80} height={24} />
                  </Box>
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="80%" />
                  <Box sx={{ mt: 2 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="60%" />
                  </Box>
                </CardContent>
                <CardActions>
                  <Skeleton variant="rectangular" width={120} height={36} />
                </CardActions>
              </Card>
            </Grid>
          ))
        ) : (
          paginatedJobs?.map((job: AnalysisJob) => (
          <Grid item xs={12} md={6} lg={4} key={job.id}>
            <Card sx={{ 
              position: 'relative',
              border: selectedJobIds.has(job.id) ? 2 : 1,
              borderColor: selectedJobIds.has(job.id) ? 'primary.main' : 'divider',
              transition: 'border-color 0.2s'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {userFilter !== 'shared' && (
                      <Checkbox
                        checked={selectedJobIds.has(job.id)}
                        onChange={() => toggleJobSelection(job.id)}
                        size="small"
                        sx={{ p: 0 }}
                      />
                    )}
                    <Typography variant="h6" component="h2">
                      Analysis #{job.id}
                    </Typography>
                  </Box>
                  <Chip 
                    icon={getStatusIcon(job.status)}
                    label={job.status.toUpperCase()}
                    color={getStatusColor(job.status)}
                    size="small"
                  />
                </Box>
                
                {/* Show Device IDs for device-based analysis, or Students/Course for course-based */}
                {job.device_ids && job.device_ids.length > 0 ? (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Device{job.device_ids.length > 1 ? 's' : ''}:</strong>{' '}
                    {job.device_ids.length <= 2 
                      ? job.device_ids.map(id => id.substring(0, 8) + '...').join(', ')
                      : `${job.device_ids.length} devices`}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Entities:</strong> {job.students?.join(', ') || 'All'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Course:</strong> {job.course_id || 'N/A'}
                    </Typography>
                  </>
                )}
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Date Range:</strong> {formatDateRange(job.date_range_start, job.date_range_end)}
                </Typography>

                {job.user_id && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {userFilter === 'shared' ? 'Shared by' : 'Created by'}:{' '}
                      <strong>{formatUserLabel(job.user_id)}</strong>
                    </Typography>
                  </Box>
                )}
                
                {job.start_time && (
                  <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                    Started: {new Date(job.start_time).toLocaleString()}
                  </Typography>
                )}
                {job.end_time && (
                  <Typography variant="caption" display="block">
                    Completed: {new Date(job.end_time).toLocaleString()}
                  </Typography>
                )}
              </CardContent>
              
              <CardActions sx={{ justifyContent: 'space-between' }}>
                <Box>
                  {job.status === 'completed' && (
                    <Button 
                      size="small" 
                      startIcon={<Visibility />}
                      onClick={() => setSelectedJob(job.id)}
                    >
                      View Results
                    </Button>
                  )}
                  {job.status === 'running' && (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                      Analysis in progress...
                    </Typography>
                  )}
                  {job.status === 'failed' && job.error_message && (
                    <Typography variant="body2" color="error" sx={{ pl: 1 }}>
                      Error: {job.error_message}
                    </Typography>
                  )}
                </Box>
                
                {/* Delete button - owner only. Sharing grants read access, so the
                    backend rejects deletes from recipients; don't offer the action. */}
                {userFilter !== 'shared' && (
                <Tooltip title={job.status === 'running' ? "Cancel & Delete" : "Delete Analysis"}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => openDeleteDialog(job)}
                    disabled={deleting && jobToDelete?.id === job.id}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))
        )}
      </Grid>

      {/* Pagination */}
      {filteredJobs.length > 0 && (
        <Box sx={{ 
          mt: 4, 
          display: 'flex', 
          justifyContent: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredJobs.length)} of {filteredJobs.length} analysis jobs
          </Typography>
          <TablePagination
            component="div"
            count={filteredJobs.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[6, 12, 24, 48]}
            labelRowsPerPage="Jobs per page:"
            showFirstButton
            showLastButton
            sx={{ 
              '& .MuiTablePagination-toolbar': { 
                minHeight: '52px',
                paddingLeft: { xs: 0, sm: 2 },
                paddingRight: { xs: 0, sm: 2 }
              },
              '& .MuiTablePagination-selectLabel': { 
                margin: 0,
                fontSize: '0.875rem'
              },
              '& .MuiTablePagination-displayedRows': { 
                margin: 0,
                fontSize: '0.875rem'
              }
            }}
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Analysis Job?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to permanently delete this analysis job and all its results? 
            This action cannot be undone.
            <br /><br />
            <strong>Job:</strong> Analysis #{jobToDelete?.id}
            <br />
            {jobToDelete?.device_ids && jobToDelete.device_ids.length > 0 ? (
              <><strong>Devices:</strong> {jobToDelete.device_ids.length} device(s)</>
            ) : (
              <><strong>Entities:</strong> {jobToDelete?.students?.join(', ') || 'All'}</>
            )}
            <br />
            <strong>Created:</strong> {jobToDelete?.start_time ? new Date(jobToDelete.start_time).toLocaleDateString() : 'Unknown'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteJob} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => !deleting && setBulkDeleteDialogOpen(false)}
        aria-labelledby="bulk-delete-dialog-title"
        aria-describedby="bulk-delete-dialog-description"
      >
        <DialogTitle id="bulk-delete-dialog-title">
          Delete {selectedJobIds.size} Analysis Jobs?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="bulk-delete-dialog-description">
            Are you sure you want to permanently delete <strong>{selectedJobIds.size}</strong> analysis job{selectedJobIds.size > 1 ? 's' : ''} and all their results? 
            This action cannot be undone.
            <br /><br />
            <strong>Jobs to delete:</strong> {Array.from(selectedJobIds).map(id => `#${id}`).join(', ')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBulkDeleteDialogOpen(false)} 
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBulkDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
          >
            {deleting ? 'Deleting...' : `Delete ${selectedJobIds.size} Jobs`}
          </Button>
        </DialogActions>
      </Dialog>
      </>
    </Box>
  );
};

export default ResultsPage;
